/**
 * Theme-driven round label rename helpers.
 *
 * When the campaign creator changes the `attemptWindow` (e.g. week → day), any
 * round whose label still matches the *theme default* for the OLD window
 * should be renamed to the equivalent default for the NEW window.
 *
 * Manually edited labels (anything that doesn't match the old default pattern)
 * are preserved verbatim — the creator's edit always wins.
 */

import type { QuizConfig, QuizRound, QuizSlot } from '@/types/form.types'

export type AttemptWindow = QuizConfig['attemptWindow']

const WINDOW_NOUN: Record<AttemptWindow, string> = {
    week: 'Week',
    day: 'Day',
    hour: 'Hour',
    minute: 'Minute',
    custom: 'Round',
}

/**
 * Default label for round at `index` (0-based) under a given `attemptWindow`.
 * Mirrors the template config's "Week 1", "Week 2"… pattern.
 */
export function defaultRoundLabel(window: AttemptWindow, index: number): string {
    return `${WINDOW_NOUN[window]} ${index + 1}`
}

/**
 * Generic `Round N` fallback used by some templates / migrated configs.
 */
function genericRoundLabel(index: number): string {
    return `Round ${index + 1}`
}

/**
 * True if `label` matches the default label for ANY position under the given
 * window. Position-tolerant on purpose — re-ordering rounds shouldn't pin a
 * label as "manually edited" when it's still in the default family.
 */
export function isDefaultLabelForWindow(label: string, window: AttemptWindow): boolean {
    if (!label) return false
    const noun = WINDOW_NOUN[window]
    const themeDefault = new RegExp(`^${noun} \\d+$`)
    if (themeDefault.test(label)) return true
    // The generic "Round N" pattern is also considered a default — applied by
    // some templates and by `normalizeQuizConfig` for legacy configs.
    if (/^Round \d+$/.test(label)) return true
    return false
}

/**
 * Return a new array of rounds with theme-default labels rewritten to match
 * `nextWindow`. Preserves manually edited labels.
 */
export function renameRoundsForWindow<T extends Pick<QuizRound, 'label'>>(
    rounds: T[],
    previousWindow: AttemptWindow,
    nextWindow: AttemptWindow,
): T[] {
    if (previousWindow === nextWindow) return rounds.map((r) => ({ ...r }))
    return rounds.map((round, index) => {
        if (isDefaultLabelForWindow(round.label, previousWindow)) {
            return { ...round, label: defaultRoundLabel(nextWindow, index) }
        }
        return { ...round }
    })
}

/**
 * Sibling helper for slots — same rule as rounds.
 */
export function renameSlotsForWindow<T extends Pick<QuizSlot, 'label'>>(
    slots: T[],
    previousWindow: AttemptWindow,
    nextWindow: AttemptWindow,
): T[] {
    if (previousWindow === nextWindow) return slots.map((s) => ({ ...s }))
    return slots.map((slot, index) => {
        if (isDefaultLabelForWindow(slot.label, previousWindow)) {
            return { ...slot, label: defaultRoundLabel(nextWindow, index) }
        }
        return { ...slot }
    })
}

// Exposed for tests and rare external callers.
export const __testing = { genericRoundLabel }
